import request from '@teetsh/app/src/utils/request';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LivretEval } from '@teetsh/app/src/containers/Livrets/types/livret';
import livretKeys from '@teetsh/app/src/containers/Livrets/queries/keys';
import { updateMatiereLivretEleve } from '@teetsh/app/src/containers/Livrets/queries/updateMatiereLivretEleve';
import { removeCompetence } from '@teetsh/app/src/containers/Livrets/queries/competences/removeCompetence';
import { useTranslation } from 'react-i18next';
import { useLSUErrorsMapping } from 'containers/Livrets/useLSUErrorsMapping';

export interface DeleteLivretEleveCompetenceDTO {
  id: string;
  matiereId: string;
  domaineId?: string;
  all?: boolean;
}

function deleteLivretEleveCompetence({
  schoolyearId,
  schoolId,
  livretEleveId,
  matiereId,
  domaineId,
  competenceId,
  all,
}: {
  schoolyearId: string;
  schoolId: string;
  livretEleveId: string;
  competenceId: string;
  matiereId: string;
  domaineId?: string;
  all?: boolean;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/livrets-eleves/${livretEleveId}/competences/${competenceId}`;
  return request(requestURL, {
    method: 'DELETE',
    body: {
      matiereId,
      domaineId,
      all,
    },
  });
}

interface Props {
  livretEleveId: string;
  livretEvalId: string;
}

export function useDeleteLivretCompetence(props: Props) {
  const { notifyError, notifySuccess } = useNotification();
  const errorsMapping = useLSUErrorsMapping();
  const { schoolyear, school } = useSchoolSchoolyear();
  const { livretEleveId, livretEvalId } = props;
  const { t } = useTranslation(['carnet']);
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: ({
      matiereId,
      domaineId,
      id: competenceId,
      all,
    }: DeleteLivretEleveCompetenceDTO) =>
      deleteLivretEleveCompetence({
        schoolyearId: schoolyear.id,
        schoolId: school.id,
        livretEleveId,
        competenceId,
        matiereId,
        domaineId,
        all,
      }),
    onMutate: async ({ id: competenceId, matiereId, domaineId }) => {
      await queryClient.cancelQueries({
        queryKey: livretKeys.livretDetail(
          schoolyear.id,
          school.id,
          livretEvalId,
        ),
      });
      const previousLivretEval = queryClient.getQueryData(
        livretKeys.livretDetail(schoolyear.id, school.id, livretEvalId),
      );
      queryClient.setQueryData<LivretEval>(
        livretKeys.livretDetail(schoolyear.id, school.id, livretEvalId),
        (oldLivretEval: LivretEval) => ({
          ...oldLivretEval,
          livretsEleves: (oldLivretEval?.livretsEleves || []).map(
            livretEleve => {
              if (livretEleve.id === livretEleveId) {
                const matiere = livretEleve.matieres.find(
                  m => m.id === matiereId,
                );
                if (!matiere) return livretEleve;
                const domaine = domaineId
                  ? matiere.domaines.find(d => d.id === domaineId)
                  : undefined;
                const newMatiere = removeCompetence(
                  matiere,
                  competenceId,
                  domaine,
                );
                return updateMatiereLivretEleve(
                  livretEleve,
                  newMatiere,
                  oldLivretEval.typePositionnement,
                  errorsMapping,
                );
              }
              return livretEleve;
            },
          ),
        }),
      );
      return { previousLivretEval };
    },
    onSuccess: (_, { all }) => {
      if (all) {
        queryClient.invalidateQueries({
          queryKey: livretKeys.livretDetail(
            schoolyear.id,
            school.id,
            livretEvalId,
          ),
        });
        notifySuccess({
          title: t('carnet:messages.competence.createSuccess'),
        });
      }
    },
    onError: () => {
      notifyError({
        title: t('carnet:messages.competence.deleteError'),
      });
    },
  });
  return mutateAsync;
}
